import { type SiteId } from '@solinkcorp/api-types';
import { Select } from '@solinkcorp/solink-components';
import { getLocalTimeZone } from '@solinkcorp/utils';
import React, { useEffect, useMemo } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import { LocationStore } from 'stores/LocationStore';
import { type callhomeCameraToEventCamera } from 'utils/CameraUtil';
import { type VideoDiscoverySite } from 'utils/types';

interface CamerasTimezoneSelectProps {
  cameras: ReturnType<typeof callhomeCameraToEventCamera>[];
  selectedLocationId: string;
  setSelectedLocationId: React.Dispatch<React.SetStateAction<SiteId>>;
}

/**
 * Renders a unique list of locations for the provided cameras prop,
 * locations are displayed by their timezone
 */
const CamerasTimezoneSelect: React.FC<CamerasTimezoneSelectProps> = ({
  cameras,
  selectedLocationId,
  setSelectedLocationId,
}) => {
  const locationsById = useFluxStore(LocationStore, (store) => store.getLocationsById());

  const uniqueLocationTimezones = useMemo((): Array<VideoDiscoverySite> => {
    const locationsByUniqueTimezones: Array<VideoDiscoverySite> = [];

    cameras.forEach((camera) => {
      const location = locationsById?.[camera.siteId];
      if (!location) {
        return;
      }

      if (
        location?.timezone &&
        !locationsByUniqueTimezones.some((device) => device.timezone === location.timezone)
      ) {
        locationsByUniqueTimezones.push(location);
      }
    });

    return locationsByUniqueTimezones;
  }, [cameras, locationsById]);

  useEffect(() => {
    const userTimezone = getLocalTimeZone();

    const locationInUserTimezone = uniqueLocationTimezones.find(
      (location) => location?.timezone === userTimezone,
    );

    setSelectedLocationId(locationInUserTimezone?.id ?? uniqueLocationTimezones[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Select
      name="ai-inspection-timezone-selector"
      value={selectedLocationId}
      options={uniqueLocationTimezones.map((location) => ({
        value: location.id,
        label: location?.timezone ?? '',
      }))}
      onChange={({ value }: any) => setSelectedLocationId(value)}
    />
  );
};

export default CamerasTimezoneSelect;
